import React, { useState, useEffect, useRef, useMemo } from "react";
import { usePopupClickOutside } from "../../../../hooks/usePopupClickOutside";
import toastr from "../../../common/toastr";
import {
  fetchContentPermissions,
  createContentPermission,
  updateContentPermission,
  deleteContentPermission,
  updateDefaultAccessLevel,
  fetchAvailableUsers,
  fetchUserGroups,
  ContentPermission,
  AvailableUser,
  ContentType,
  AccessLevel
} from "../../API/api";

interface ManageContentPermissionsModalProps {
  contentType: ContentType;
  contentId: number;
  contentName: string;
  onClose: () => void;
}

const userKey = (u: AvailableUser): string => {
  const type = u.type || "User";
  return `${type}:${u.id}`;
};

const sameUser = (a: AvailableUser, b: AvailableUser): boolean =>
  userKey(a) === userKey(b);

const ACCESS_LEVELS: {
  value: AccessLevel | null;
  label: string;
  description: string;
}[] = [
  {
    value: null,
    label: "Restricted",
    description: "Only the members invited below will have access"
  },
  {
    value: "viewer",
    label: "Viewer",
    description: "All the team members can view and download media"
  },
  {
    value: "contributor",
    label: "Contributor",
    description:
      "All the team members can upload photos to albums created by them"
  },
  {
    value: "manager",
    label: "Manager",
    description:
      "All the team members have all the access including managing access"
  }
];

const USER_ACCESS_LEVELS: {
  value: AccessLevel;
  label: string;
  description: string;
  icon: string;
}[] = [
  {
    value: "viewer",
    label: "Viewer",
    description: "Can view and download photos",
    icon: "fa-eye"
  },
  {
    value: "contributor",
    label: "Contributor",
    description: "Can upload photos to their own albums",
    icon: "fa-upload"
  },
  {
    value: "manager",
    label: "Manager",
    description: "Full access including managing permissions",
    icon: "fa-cog"
  }
];

const getInitials = (name: string): string => {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
};

const getAvatarClass = (level: AccessLevel): string => {
  switch (level) {
    case "manager":
      return "permissions-modal__avatar--manager";
    case "contributor":
      return "permissions-modal__avatar--contributor";
    default:
      return "permissions-modal__avatar--viewer";
  }
};

// Add user flow steps
type AddUserStep = "search" | "select-access";

const ManageContentPermissionsModal: React.FC<
  ManageContentPermissionsModalProps
> = ({ contentType, contentId, contentName, onClose }) => {
  const popupRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(true);
  const [permissions, setPermissions] = useState<ContentPermission[]>([]);
  const [defaultAccessLevel, setDefaultAccessLevel] = useState<string | null>(
    null
  );
  const [showAddUser, setShowAddUser] = useState(false);
  const [addUserStep, setAddUserStep] = useState<AddUserStep>("search");
  const [searchQuery, setSearchQuery] = useState("");
  const [availableUsers, setAvailableUsers] = useState<AvailableUser[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState<AvailableUser[]>([]);
  const [selectedAccessLevel, setSelectedAccessLevel] =
    useState<AccessLevel>("viewer");
  const [savingPermission, setSavingPermission] = useState(false);
  const [showAccessDropdown, setShowAccessDropdown] = useState(false);
  const [updatingDefaultAccess, setUpdatingDefaultAccess] = useState(false);
  const accessDropdownRef = useRef<HTMLDivElement>(null);

  usePopupClickOutside(popupRef as React.RefObject<HTMLElement>, () =>
    onClose()
  );

  useEffect(() => {
    loadPermissions();
  }, [contentType, contentId]);

  useEffect(() => {
    if (showAddUser && addUserStep === "search") {
      const debounce = setTimeout(() => {
        searchUsers();
      }, 300);
      return () => clearTimeout(debounce);
    }
    return undefined;
  }, [searchQuery, showAddUser, addUserStep]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        accessDropdownRef.current &&
        !accessDropdownRef.current.contains(event.target as Node)
      ) {
        setShowAccessDropdown(false);
      }
    };

    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setShowAccessDropdown(false);
      }
    };

    if (showAccessDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("keydown", handleEscKey);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
        document.removeEventListener("keydown", handleEscKey);
      };
    }
  }, [showAccessDropdown]);

  const selectedAccessOption = useMemo(() => {
    return (
      ACCESS_LEVELS.find(option => option.value === defaultAccessLevel) || {
        label: "Restricted",
        description: "Only the members invited below will have access",
        value: null
      }
    );
  }, [defaultAccessLevel]);

  const loadPermissions = async () => {
    setLoading(true);
    try {
      const data = await fetchContentPermissions(contentType, contentId);
      setPermissions(data.permissions);
      setDefaultAccessLevel(data.defaultAccessLevel);
    } catch (error) {
      toastr.error("Failed to load permissions");
    } finally {
      setLoading(false);
    }
  };

  const searchUsers = async () => {
    setSearchLoading(true);
    try {
      // Fetch both users and groups in parallel
      const [users, groups] = await Promise.all([
        fetchAvailableUsers(contentType, contentId, searchQuery),
        fetchUserGroups()
      ]);

      // Get existing group IDs that already have permissions
      const existingGroupIds = permissions
        .filter(p => p.granteeType === "UserGroup")
        .map(p => p.granteeId);

      // Transform groups to AvailableUser format and filter out existing ones
      const availableGroups: AvailableUser[] = groups
        .filter(group => !existingGroupIds.includes(group.id))
        .map(group => ({
          id: group.id,
          name: group.name,
          email: group.memberCount
            ? `${group.memberCount} ${
                group.memberCount === 1 ? "person" : "people"
              }`
            : "0 people",
          type: "UserGroup" as const,
          memberCount: group.memberCount
        }));

      // Filter groups based on search query
      const filteredGroups = searchQuery
        ? availableGroups.filter(group =>
            group.name.toLowerCase().includes(searchQuery.toLowerCase())
          )
        : availableGroups;

      // Combine users and groups
      const combined = [...users, ...filteredGroups];
      setAvailableUsers(combined);
    } catch (error) {
      // Silent failure - user can retry search
    } finally {
      setSearchLoading(false);
    }
  };

  const handleToggleUser = (user: AvailableUser) => {
    setSelectedUsers(prev =>
      prev.some(u => sameUser(u, user))
        ? prev.filter(u => !sameUser(u, user))
        : [...prev, user]
    );
  };

  const handleRemoveSelectedUser = (user: AvailableUser) => {
    setSelectedUsers(prev => prev.filter(u => !sameUser(u, user)));
  };

  const handleProceedToAccessLevel = () => {
    if (selectedUsers.length > 0) {
      setAddUserStep("select-access");
    }
  };

  const handleBackToSearch = () => {
    setAddUserStep("search");
    setSelectedAccessLevel("viewer");
  };

  const handleAddPermission = async () => {
    if (selectedUsers.length === 0) return;

    setSavingPermission(true);
    try {
      for (const user of selectedUsers) {
        await createContentPermission(
          contentType,
          contentId,
          user.id,
          selectedAccessLevel,
          user.type || "User"
        );
      }
      await loadPermissions();
      // Reset the add user flow
      const count = selectedUsers.length;
      setShowAddUser(false);
      setAddUserStep("search");
      setSearchQuery("");
      setAvailableUsers([]);
      setSelectedUsers([]);
      setSelectedAccessLevel("viewer");
      toastr.success(
        count === 1
          ? `${selectedUsers[0].name} has been added`
          : `${count} users and groups have been added`
      );
    } catch (error: any) {
      toastr.error(error.message || "Failed to add permissions");
    } finally {
      setSavingPermission(false);
    }
  };

  const isUserSelected = (user: AvailableUser) =>
    selectedUsers.some(u => sameUser(u, user));

  const handleUpdatePermission = async (
    permissionId: number,
    newLevel: AccessLevel
  ) => {
    try {
      await updateContentPermission(
        contentType,
        contentId,
        permissionId,
        newLevel
      );
      setPermissions(prev =>
        prev.map(p =>
          p.id === permissionId ? { ...p, accessLevel: newLevel } : p
        )
      );
      toastr.success("Access level updated");
    } catch (error: any) {
      toastr.error(error.message || "Failed to update permission");
    }
  };

  const handleRemovePermission = async (permissionId: number) => {
    if (!confirm("Remove this user's access?")) return;

    try {
      await deleteContentPermission(contentType, contentId, permissionId);
      setPermissions(prev => prev.filter(p => p.id !== permissionId));
      toastr.success("Access removed");
    } catch (error: any) {
      toastr.error(error.message || "Failed to remove permission");
    }
  };

  const handleDefaultAccessChange = async (newLevel: AccessLevel | null) => {
    setUpdatingDefaultAccess(true);
    setShowAccessDropdown(false);
    try {
      const result = await updateDefaultAccessLevel(
        contentType,
        contentId,
        newLevel
      );
      setDefaultAccessLevel(result.defaultAccessLevel);
      toastr.success(
        newLevel
          ? `Default access set to ${newLevel}`
          : "Default access disabled"
      );
    } catch (error: any) {
      toastr.error(error.message || "Failed to update default access");
    } finally {
      setUpdatingDefaultAccess(false);
    }
  };

  const handleCancelAddUser = () => {
    setShowAddUser(false);
    setAddUserStep("search");
    setSearchQuery("");
    setAvailableUsers([]);
    setSelectedUsers([]);
    setSelectedAccessLevel("viewer");
  };

  return (
    <div>
      <div
        className="modal fade in geo-modal permissions-modal geo-modal--scroll"
        role="dialog"
        style={{ display: "block" }}
      >
        <div className="modal-dialog" ref={popupRef}>
          <div className="modal-content">
            {/* Header */}
            <div className="modal-header permissions-modal__header">
              <h2 className="permissions-modal__title">Manage Access</h2>
              <p className="permissions-modal__subtitle">{contentName}</p>
              <button
                type="button"
                className="close"
                data-dismiss="modal"
                aria-label="Close"
                onClick={onClose}
              >
                <span aria-hidden="true">&times;</span>
              </button>
            </div>
            <div className="permissions-modal__container">
              {/* Body */}
              <div className="permissions-modal__body">
                {loading ? (
                  <div className="permissions-modal__loading">
                    <span className="fa fa-spinner fa-spin fa-2x"></span>
                    <p>Loading permissions...</p>
                  </div>
                ) : (
                  <>
                    {/* Default Access Section */}
                    <div className="permissions-modal__section permissions-modal__section--default-access">
                      <h3 className="permissions-modal__section-title">
                        Default Access
                      </h3>
                      <p className="permissions-modal__section-desc">
                        Grant all account users in this team a default access
                        level
                      </p>
                      <div
                        className="permissions-modal__access-dropdown"
                        ref={accessDropdownRef}
                      >
                        {/* Dropdown Trigger */}
                        <button
                          type="button"
                          className={`permissions-modal__dropdown-trigger ${
                            updatingDefaultAccess
                              ? "permissions-modal__dropdown-trigger--disabled"
                              : ""
                          }`}
                          onClick={() =>
                            setShowAccessDropdown(!showAccessDropdown)
                          }
                          disabled={updatingDefaultAccess}
                          aria-haspopup="listbox"
                          aria-expanded={showAccessDropdown}
                        >
                          <div className="permissions-modal__dropdown-trigger-content">
                            <span className="permissions-modal__dropdown-label">
                              {selectedAccessOption.label}
                            </span>
                            <span className="permissions-modal__dropdown-desc">
                              {selectedAccessOption.description}
                            </span>
                          </div>
                          <span
                            className={`fa fa-angle-down permissions-modal__dropdown-icon ${
                              showAccessDropdown
                                ? "permissions-modal__dropdown-icon--open"
                                : ""
                            }`}
                            aria-hidden="true"
                          ></span>
                        </button>

                        {/* Loading Overlay */}
                        {updatingDefaultAccess && (
                          <div
                            className="permissions-modal__dropdown-loading"
                            role="status"
                            aria-live="polite"
                          >
                            <span
                              className="fa fa-spinner fa-spin"
                              aria-hidden="true"
                            ></span>
                            <span className="permissions-modal__dropdown-loading-text">
                              Updating
                            </span>
                          </div>
                        )}

                        {/* Dropdown Menu */}
                        {showAccessDropdown && !updatingDefaultAccess && (
                          <div
                            className="permissions-modal__dropdown-menu"
                            role="listbox"
                          >
                            {ACCESS_LEVELS.map(option => (
                              <div
                                key={option.value || "restricted"}
                                className={`permissions-modal__dropdown-option ${
                                  (option.value === null &&
                                    !defaultAccessLevel) ||
                                  defaultAccessLevel === option.value
                                    ? "permissions-modal__dropdown-option--selected"
                                    : ""
                                }`}
                                onClick={() =>
                                  handleDefaultAccessChange(option.value)
                                }
                                role="option"
                                aria-selected={
                                  (option.value === null &&
                                    !defaultAccessLevel) ||
                                  defaultAccessLevel === option.value
                                }
                              >
                                <div className="permissions-modal__dropdown-option-content">
                                  <span className="permissions-modal__dropdown-option-title">
                                    {option.label}
                                  </span>
                                  <span className="permissions-modal__dropdown-option-desc">
                                    {option.description}
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Individual Access Section */}
                    <div className="permissions-modal__section">
                      <div className="permissions-modal__section-header">
                        <h3 className="permissions-modal__section-title">
                          Individual Access
                        </h3>
                        {!showAddUser && (
                          <button
                            className="geo-btn geo-btn--secondary geo-btn--caps geo-btn--small geo-btn--add-user"
                            onClick={() => setShowAddUser(true)}
                          >
                            <span className="fa fa-plus"></span>
                            Add Users & Groups
                          </button>
                        )}
                      </div>

                      {/* Add User Form - Step-based flow */}
                      {showAddUser && (
                        <div className="permissions-modal__add-user">
                          {addUserStep === "search" ? (
                            <>
                              {/* Step 1: Search and select users */}
                              <div className="permissions-modal__step-header">
                                <span className="permissions-modal__step-label">
                                  Step 1 of 2
                                </span>
                                <span className="permissions-modal__step-title">
                                  Select users
                                </span>
                              </div>

                              {/* Selected users chips */}
                              {selectedUsers.length > 0 && (
                                <div className="permissions-modal__selected-chips">
                                  {selectedUsers.map(user => (
                                    <div
                                      key={userKey(user)}
                                      className="permissions-modal__chip"
                                    >
                                      <span className="permissions-modal__chip-name">
                                        {user.name}
                                      </span>
                                      <button
                                        className="permissions-modal__chip-remove"
                                        onClick={() =>
                                          handleRemoveSelectedUser(user)
                                        }
                                      >
                                        <span className="fa fa-times"></span>
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              )}

                              <input
                                type="text"
                                className="permissions-modal__search-input"
                                placeholder="Search by name, email or group"
                                value={searchQuery}
                                onChange={e => setSearchQuery(e.target.value)}
                                autoFocus
                              />
                              <div className="permissions-modal__search-results">
                                {searchLoading ? (
                                  <div className="permissions-modal__search-loading">
                                    <span className="fa fa-spinner fa-spin"></span>
                                  </div>
                                ) : availableUsers.length === 0 ? (
                                  <div className="permissions-modal__search-empty">
                                    {searchQuery
                                      ? "No users found"
                                      : "Type to search for users"}
                                  </div>
                                ) : (
                                  availableUsers.map(user => (
                                    <div
                                      key={userKey(user)}
                                      className={`permissions-modal__user-result permissions-modal__user-result--selectable ${
                                        isUserSelected(user)
                                          ? "permissions-modal__user-result--selected"
                                          : ""
                                      }`}
                                      onClick={() => handleToggleUser(user)}
                                    >
                                      <div className="permissions-modal__user-checkbox">
                                        {isUserSelected(user) && (
                                          <span className="fa fa-check"></span>
                                        )}
                                      </div>
                                      <div className="permissions-modal__user-info">
                                        <div
                                          className={`permissions-modal__avatar ${
                                            user.type === "UserGroup"
                                              ? "permissions-modal__avatar--group"
                                              : "permissions-modal__avatar--default"
                                          }`}
                                        >
                                          {getInitials(user.name)}
                                        </div>
                                        <div className="permissions-modal__user-details">
                                          <div className="permissions-modal__user-name-row">
                                            <p className="permissions-modal__user-name">
                                              {user.name}
                                            </p>
                                            {user.type === "UserGroup" && (
                                              <span className="permissions-modal__group-badge">
                                                Group
                                              </span>
                                            )}
                                          </div>
                                          <p className="permissions-modal__user-email">
                                            {user.type === "UserGroup" &&
                                            user.memberCount
                                              ? `${user.memberCount} ${
                                                  user.memberCount === 1
                                                    ? "person"
                                                    : "people"
                                                }`
                                              : user.email}
                                          </p>
                                        </div>
                                      </div>
                                    </div>
                                  ))
                                )}
                              </div>
                              <div className="permissions-modal__add-actions permissions-modal__add-actions--spaced">
                                <button
                                  className="geo-btn geo-btn--grey geo-btn--small"
                                  onClick={handleCancelAddUser}
                                >
                                  Cancel
                                </button>
                                <button
                                  className="geo-btn geo-btn--secondary geo-btn--small geo-btn--add-user"
                                  onClick={handleProceedToAccessLevel}
                                  disabled={selectedUsers.length === 0}
                                >
                                  Next
                                  <span className="fa fa-arrow-right"></span>
                                </button>
                              </div>
                            </>
                          ) : (
                            <>
                              {/* Step 2: Choose access level */}
                              <div className="permissions-modal__step-header">
                                <span className="permissions-modal__step-label">
                                  Step 2 of 2
                                </span>
                                <span className="permissions-modal__step-title">
                                  Choose access level
                                </span>
                              </div>

                              {/* Selected users display */}
                              <div className="permissions-modal__selected-users-summary">
                                <div className="permissions-modal__selected-users-avatars">
                                  {selectedUsers.slice(0, 3).map(user => (
                                    <div
                                      key={userKey(user)}
                                      className={`permissions-modal__avatar ${
                                        user.type === "UserGroup"
                                          ? "permissions-modal__avatar--group"
                                          : "permissions-modal__avatar--default"
                                      } permissions-modal__avatar--stacked`}
                                      title={user.name}
                                    >
                                      {getInitials(user.name)}
                                    </div>
                                  ))}
                                  {selectedUsers.length > 3 && (
                                    <div className="permissions-modal__avatar permissions-modal__avatar--more">
                                      +{selectedUsers.length - 3}
                                    </div>
                                  )}
                                </div>
                                <div className="permissions-modal__selected-users-info">
                                  <p className="permissions-modal__selected-users-text">
                                    {selectedUsers.length === 1
                                      ? selectedUsers[0].name
                                      : `${selectedUsers.length} users and groups selected`}
                                  </p>
                                  {selectedUsers.length === 1 && (
                                    <p className="permissions-modal__selected-users-email">
                                      {selectedUsers[0].type === "UserGroup" &&
                                      selectedUsers[0].memberCount
                                        ? `${selectedUsers[0].memberCount} ${
                                            selectedUsers[0].memberCount === 1
                                              ? "person"
                                              : "people"
                                          }`
                                        : selectedUsers[0].email}
                                    </p>
                                  )}
                                </div>
                                <button
                                  className="permissions-modal__change-user"
                                  onClick={handleBackToSearch}
                                >
                                  Change
                                </button>
                              </div>

                              {/* Access level options */}
                              <div className="permissions-modal__access-options">
                                {USER_ACCESS_LEVELS.map(option => (
                                  <div
                                    key={option.value}
                                    className={`permissions-modal__access-option ${
                                      selectedAccessLevel === option.value
                                        ? "permissions-modal__access-option--selected"
                                        : ""
                                    }`}
                                    onClick={() =>
                                      setSelectedAccessLevel(option.value)
                                    }
                                  >
                                    <div className="permissions-modal__access-option-content">
                                      <div className="permissions-modal__access-option-header">
                                        <span
                                          className={`fa ${option.icon} permissions-modal__access-option-icon`}
                                        ></span>
                                        <span className="permissions-modal__access-option-label">
                                          {option.label}
                                        </span>
                                      </div>
                                      <p className="permissions-modal__access-option-desc">
                                        {option.description}
                                      </p>
                                    </div>
                                    <div className="permissions-modal__access-option-radio">
                                      {selectedAccessLevel === option.value && (
                                        <span className="fa fa-check"></span>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>

                              <div className="permissions-modal__add-actions permissions-modal__add-actions--spaced">
                                <button
                                  className="geo-btn geo-btn--grey geo-btn--add-user"
                                  onClick={handleBackToSearch}
                                >
                                  <span className="fa fa-arrow-left"></span>
                                  Back
                                </button>
                                <button
                                  className="geo-btn geo-btn--secondary geo-btn--add-user"
                                  onClick={handleAddPermission}
                                  disabled={savingPermission}
                                >
                                  {savingPermission ? (
                                    <span className="fa fa-spinner fa-spin"></span>
                                  ) : (
                                    <>
                                      <span className="fa fa-plus"></span>
                                      Add Selected
                                    </>
                                  )}
                                </button>
                              </div>
                            </>
                          )}
                        </div>
                      )}

                      {/* Permissions List */}
                      {permissions.length === 0 ? (
                        <div className="permissions-modal__empty">
                          <div className="permissions-modal__empty-icon">
                            <span className="fa fa-users"></span>
                          </div>
                          <p className="permissions-modal__empty-text">
                            No individual access granted yet.
                            <br />
                            Click "Add User" to grant specific users access.
                          </p>
                        </div>
                      ) : (
                        <div className="permissions-modal__list">
                          {permissions.map(permission => (
                            <div
                              key={permission.id}
                              className="permissions-modal__permission-item"
                            >
                              <div className="permissions-modal__user-info">
                                <div
                                  className={`permissions-modal__avatar ${
                                    permission.granteeType === "UserGroup"
                                      ? "permissions-modal__avatar--group"
                                      : getAvatarClass(
                                          permission.accessLevel as AccessLevel
                                        )
                                  }`}
                                >
                                  {getInitials(permission.granteeName || "")}
                                </div>
                                <div className="permissions-modal__user-details">
                                  <div className="permissions-modal__user-name-row">
                                    <p className="permissions-modal__user-name">
                                      {permission.granteeName || "Unknown"}
                                    </p>
                                    {permission.granteeType === "UserGroup" && (
                                      <span className="permissions-modal__group-badge">
                                        Group
                                      </span>
                                    )}
                                  </div>
                                  {permission.granteeEmail && (
                                    <p className="permissions-modal__user-email">
                                      {permission.granteeEmail}
                                    </p>
                                  )}
                                </div>
                              </div>
                              <div className="permissions-modal__permission-actions">
                                <div className="geo-form__selectwrap select select-wrap permissions-modal__selwrap">
                                  <select
                                    className="geo-form__select permissions-modal__permission-select"
                                    value={permission.accessLevel}
                                    onChange={e =>
                                      handleUpdatePermission(
                                        permission.id,
                                        e.target.value as AccessLevel
                                      )
                                    }
                                  >
                                    {USER_ACCESS_LEVELS.map(option => (
                                      <option
                                        key={option.value}
                                        value={option.value}
                                      >
                                        {option.label}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                                <button
                                  className="permissions-modal__remove-btn"
                                  onClick={() =>
                                    handleRemovePermission(permission.id)
                                  }
                                  title="Remove access"
                                >
                                  <span className="fa fa-trash"></span>
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>

              {/* Footer */}
              {!loading && (
                <div className="permissions-modal__footer">
                  <button
                    className="geo-btn geo-btn--secondary geo-btn--caps"
                    onClick={onClose}
                  >
                    Done
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade in" />
    </div>
  );
};

export default ManageContentPermissionsModal;
